import React, { createContext, useContext, useState } from 'react';
import { track, TrackingEvents } from '../utils/analytics';
import { useMountEffect } from '../hooks/useMountEffect';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const applyTheme = (nextTheme: Theme): void => {
    localStorage.setItem('dashboardTheme', nextTheme);
    const action = nextTheme === 'dark' ? 'add' : 'remove';
    document.body.classList[action]('theme-dark');
    document.documentElement.classList[action]('theme-dark');
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(() => {
        const stored = localStorage.getItem('dashboardTheme');
        if (stored === 'dark' || stored === 'light') return stored as Theme;
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? 'dark' : 'light';
    });

    useMountEffect(() => {
        applyTheme(theme);
    });

    const toggleTheme = () => setThemeState(prev => {
        const newTheme = prev === 'dark' ? 'light' : 'dark';
        track(TrackingEvents.THEME_CHANGED, { new_theme: newTheme });
        applyTheme(newTheme);
        return newTheme;
    });
    const setTheme = (t: Theme) => {
        track(TrackingEvents.THEME_CHANGED, { new_theme: t });
        setThemeState(t);
        applyTheme(t);
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

/**
 * Read the theme. Inside the app this resolves the provider value; on surfaces
 * that render outside `ThemeProvider` (public doc pages, isolated tests) it
 * falls back to a light default rather than throwing, so brand chrome like
 * `BrandLogo` can render anywhere.
 */
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        return { theme: 'light', toggleTheme: () => undefined, setTheme: () => undefined };
    }
    return context;
};
